import { supabase } from "@/integrations/supabase/client";

export interface PlatformStats {
  companiesCount: number;
  bookingsCount: number;
  reviewsCount: number;
  citiesCount: number;
  averageRating: number;
}

// Placeholder stats che rinforzano social proof
const PLACEHOLDER_STATS: PlatformStats = {
  companiesCount: 50,
  bookingsCount: 500,
  reviewsCount: 1200,
  citiesCount: 6,
  averageRating: 4.9,
};

/**
 * Recupera statistiche reali dal DB
 * Se non disponibili, usa placeholder
 */
export async function getPlatformStats(): Promise<PlatformStats> {
  try {
    // Query parallele per performance
    const [companiesResult, bookingsResult, reviewsResult, citiesResult] = await Promise.all([
      // Count aziende attive
      supabase
        .from("companies")
        .select("id", { count: "exact", head: true })
        .eq("is_active", true)
        .eq("is_deleted", false),
      
      // Count prenotazioni (ultimi 30 giorni)
      supabase
        .from("bookings")
        .select("id", { count: "exact", head: true })
        .gte("created_at", new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
        .neq("status", "cancelled"),
      
      // Count reviews totali
      supabase
        .from("reviews")
        .select("id, rating", { count: "exact" })
        .limit(1000), // Limite per calcolo rating medio
      
      // Count città uniche dalle aziende
      supabase
        .from("companies")
        .select("city")
        .eq("is_active", true)
        .eq("is_deleted", false),
    ]);

    const companiesCount = companiesResult.data ? (companiesResult.count || 0) : 0;
    const bookingsCount = bookingsResult.data ? (bookingsResult.count || 0) : 0;
    const reviewsCount = reviewsResult.data ? (reviewsResult.count || 0) : 0;
    
    // Calcola rating medio
    let averageRating = 4.9; // default
    if (reviewsResult.data && Array.isArray(reviewsResult.data) && reviewsResult.data.length > 0) {
      const ratings = reviewsResult.data
        .map((r: any) => r.rating)
        .filter((r: number) => r && r >= 1 && r <= 5);
      if (ratings.length > 0) {
        averageRating = Math.round((ratings.reduce((a: number, b: number) => a + b, 0) / ratings.length) * 10) / 10;
      }
    }
    
    // Count città uniche
    let citiesCount = 0;
    if (citiesResult.data && Array.isArray(citiesResult.data)) {
      const uniqueCities = new Set(
        citiesResult.data
          .map((c: any) => c.city)
          .filter((city: string) => city && city.trim())
      );
      citiesCount = uniqueCities.size;
    }

    // Se abbiamo dati reali significativi, usiamoli
    // Altrimenti usiamo placeholder che rinforzano social proof
    if (companiesCount > 0 || bookingsCount > 0 || reviewsCount > 0) {
      return {
        companiesCount: companiesCount || PLACEHOLDER_STATS.companiesCount,
        bookingsCount: bookingsCount || PLACEHOLDER_STATS.bookingsCount,
        reviewsCount: reviewsCount || PLACEHOLDER_STATS.reviewsCount,
        citiesCount: citiesCount || PLACEHOLDER_STATS.citiesCount,
        averageRating,
      };
    }

    return PLACEHOLDER_STATS;
  } catch (error) {
    console.warn("Error fetching platform stats:", error);
    return PLACEHOLDER_STATS;
  }
}
